/* Waggle Enrich — theme.js
   Shared helpers, overlays (<dialog> drawers, search, zoom), header scroll states, the Shop panel,
   accordions and quantity steppers. Vanilla ES module, no dependencies. Other modules talk to it
   through window.Waggle. Motion: transform / opacity / clip-path; reduced motion gets opacity ≤ 150ms. */

const W = (window.Waggle = window.Waggle || {});
const doc = document;
const root = doc.documentElement;
W.routes = W.routes || {};
W.settings = W.settings || {};
W.strings = W.strings || {};

/* ---------- Helpers ---------- */
const mq = (query) => window.matchMedia(query);
W.reducedMotion = () => mq('(prefers-reduced-motion: reduce)').matches;
W.finePointer = () => mq('(pointer: fine)').matches;
W.qs = (selector, scope = doc) => scope.querySelector(selector);
W.qsa = (selector, scope = doc) => Array.from(scope.querySelectorAll(selector));
W.wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
W.ease = {
  out: 'cubic-bezier(0.22, 1, 0.36, 1)',
  inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
  in: 'cubic-bezier(0.5, 0, 0.75, 0)',
};

/* WAAPI wrapper. Under reduced motion only opacity survives, capped at 150ms. */
W.animate = (el, keyframes, options = {}) => {
  if (!el || typeof el.animate !== 'function') return null;
  if (W.reducedMotion()) {
    if (!keyframes.some((frame) => 'opacity' in frame)) return null;
    const frames = keyframes.map((frame) => ('opacity' in frame ? { opacity: frame.opacity } : {}));
    return el.animate(frames, { duration: Math.min(options.duration || 150, 150), fill: options.fill || 'none' });
  }
  return el.animate(keyframes, options);
};
W.finished = (animation) => (animation ? animation.finished.catch(() => {}) : Promise.resolve());

W.money = (cents) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: W.currency || 'GBP' }).format((Number(cents) || 0) / 100);

W.announce = (message) => {
  const region = doc.getElementById('LiveRegion');
  if (!region || !message) return;
  region.textContent = '';
  setTimeout(() => {
    region.textContent = message;
  }, 60);
};

W.emit = (name, detail = {}) => doc.dispatchEvent(new CustomEvent(name, { detail }));

/* Text roll: old value slides up and out, new value slides up in (180ms each). */
W.rollText = (el, text) => {
  if (!el) return;
  const previous = el.textContent.trim();
  if (previous === text) return;
  if (W.reducedMotion() || !previous) {
    el.textContent = text;
    return;
  }
  const roll = doc.createElement('span');
  roll.className = 'roll';
  const oldSpan = doc.createElement('span');
  oldSpan.className = 'roll__old';
  oldSpan.textContent = previous;
  const newSpan = doc.createElement('span');
  newSpan.textContent = text;
  roll.append(oldSpan, newSpan);
  el.replaceChildren(roll);
  oldSpan.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-100%)', opacity: 0 }], {
    duration: 180,
    easing: W.ease.in,
    fill: 'forwards',
  });
  newSpan
    .animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], {
      duration: 180,
      delay: 60,
      easing: W.ease.out,
      fill: 'backwards',
    })
    .finished.then(() => {
      el.textContent = text;
    })
    .catch(() => {});
};

W.bump = (el) => W.animate(el, [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 320, easing: W.ease.out });

/* ---------- Overlays ---------- */
const overlays = new Map();
const openStack = [];

class Overlay {
  constructor(dialog) {
    this.el = dialog;
    this.panel = dialog.querySelector('[data-panel]');
    this.scrim = dialog.querySelector('.drawer__scrim');
    this.type =
      dialog.dataset.overlayType ||
      (dialog.classList.contains('drawer--left') ? 'left' : dialog.classList.contains('drawer--right') ? 'right' : 'fade');
    this.trigger = null;
    this.background = [];
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    dialog.addEventListener('click', (event) => {
      const closer = event.target.closest('[data-close]');
      if (closer && dialog.contains(closer)) {
        event.preventDefault();
        this.close();
      }
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && dialog.open) {
        const focusable = W.qsa('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])', dialog)
          .filter((node) => !node.closest('[hidden]') && node.getClientRects().length);
        if (focusable.length) {
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      }
      const closer = event.target.closest('a[data-close]');
      if (closer && event.key === ' ') {
        event.preventDefault();
        this.close();
      }
    });
  }

  open(trigger) {
    if (this.el.open) return;
    this.trigger = trigger || doc.activeElement;
    if (typeof this.el.showModal === 'function') this.el.showModal();
    else this.el.setAttribute('open', '');
    if (this.el.id === 'CartDrawer') {
      this.background = Array.from(doc.body.children).filter((node) => node !== this.el && node.id !== 'LiveRegion' && node.tagName !== 'SCRIPT' && !node.contains(this.el) && !node.hasAttribute('inert'));
      this.background.forEach((node) => { node.inert = true; });
    }
    this.el.querySelector('[data-close]')?.focus({ preventScroll: true });
    openStack.push(this);
    root.classList.add('is-locked');
    if (this.trigger && this.trigger.setAttribute && this.trigger.hasAttribute('aria-controls')) {
      this.trigger.setAttribute('aria-expanded', 'true');
    }
    W.emit('overlay:open', { id: this.el.id });
    return this.enter();
  }

  enter() {
    if (W.reducedMotion()) return W.finished(this.el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 120 }));
    if (this.type === 'zoom') return Promise.resolve();
    const animations = [];
    if (this.scrim) {
      animations.push(this.scrim.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 400, easing: W.ease.standard }));
    }
    if (this.panel) {
      const from =
        this.type === 'left' ? 'translateX(-100%)' : this.type === 'search' ? 'translateY(-24px)' : 'translateX(100%)';
      const frames =
        this.type === 'search'
          ? [
              { clipPath: 'inset(0 0 100% 0)', transform: from },
              { clipPath: 'inset(0 0 0% 0)', transform: 'translateY(0)' },
            ]
          : [{ transform: from }, { transform: 'translate(0, 0)' }];
      animations.push(this.panel.animate(frames, { duration: 520, easing: W.ease.out }));
    }
    W.qsa('[data-stagger], [data-cart-lines] > li', this.el).slice(0, 12).forEach((item, index) => {
      animations.push(
        item.animate([{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }], {
          duration: 420,
          delay: 120 + Math.min(index, 6) * 50,
          easing: W.ease.out,
          fill: 'backwards',
        })
      );
    });
    const foot = this.el.querySelector('[data-cart-foot]');
    if (foot) {
      animations.push(
        foot.animate([{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }], {
          duration: 420,
          delay: 160,
          easing: W.ease.out,
          fill: 'backwards',
        })
      );
    }
    return Promise.all(animations.map(W.finished));
  }

  exit() {
    if (W.reducedMotion()) return W.finished(this.el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, fill: 'forwards' }));
    if (this.type === 'zoom') {
      return W.finished(this.el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 250, easing: W.ease.in, fill: 'forwards' }));
    }
    const animations = [];
    if (this.scrim) {
      animations.push(this.scrim.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 280, easing: W.ease.in, fill: 'forwards' }));
    }
    if (this.panel) {
      const to = this.type === 'left' ? 'translateX(-100%)' : this.type === 'search' ? 'translateY(-16px)' : 'translateX(100%)';
      const frames =
        this.type === 'search'
          ? [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: to }]
          : [{ transform: 'translate(0, 0)' }, { transform: to }];
      animations.push(this.panel.animate(frames, { duration: 360, easing: W.ease.in, fill: 'forwards' }));
    }
    return Promise.all(animations.map(W.finished));
  }

  async close() {
    if (!this.el.open || this.closing) return;
    this.closing = true;
    await this.exit();
    this.el.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
    this.el.close();
    this.background.forEach((node) => { node.inert = false; });
    this.background = [];
    this.closing = false;
    const index = openStack.indexOf(this);
    if (index > -1) openStack.splice(index, 1);
    if (!openStack.length) root.classList.remove('is-locked');
    if (this.trigger && this.trigger.setAttribute && this.trigger.hasAttribute('aria-expanded')) {
      this.trigger.setAttribute('aria-expanded', 'false');
    }
    if (this.trigger && typeof this.trigger.focus === 'function' && doc.contains(this.trigger)) {
      this.trigger.focus({ preventScroll: true });
    }
    W.emit('overlay:close', { id: this.el.id });
  }
}

const getOverlay = (id) => {
  const dialog = doc.getElementById(id);
  if (!dialog || dialog.tagName !== 'DIALOG') return null;
  if (!overlays.has(dialog)) overlays.set(dialog, new Overlay(dialog));
  return overlays.get(dialog);
};

W.overlays = {
  open: (id, trigger) => getOverlay(id)?.open(trigger),
  close: (id) => getOverlay(id)?.close(),
  get: getOverlay,
  isOpen: () => openStack.length > 0,
};

/* Links and buttons with data-open="DialogId". Links keep working without JS (anchor or page). */
const upgradeTrigger = (trigger) => {
  if (trigger.dataset.openBound) return;
  trigger.dataset.openBound = '1';
  if (trigger.tagName === 'A') trigger.setAttribute('role', 'button');
  if (trigger.hasAttribute('aria-controls')) trigger.setAttribute('aria-expanded', 'false');
};
W.qsa('[data-open]').forEach(upgradeTrigger);
W.qsa('a[data-close]').forEach((link) => link.setAttribute('role', 'button'));

doc.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-open]');
  if (!trigger) return;
  const overlay = getOverlay(trigger.dataset.open);
  if (!overlay) return;
  event.preventDefault();
  overlay.open(trigger);
});
doc.addEventListener('keydown', (event) => {
  if (event.key !== ' ') return;
  const trigger = event.target.closest('a[data-open]');
  if (!trigger) return;
  event.preventDefault();
  getOverlay(trigger.dataset.open)?.open(trigger);
});

/* ---------- Header: scrolled, hide on scroll down, show on scroll up ---------- */
const head = W.qs('[data-site-head]');
W.header = {
  show: () => head && head.classList.remove('is-hidden'),
};
if (head) {
  let lastY = window.scrollY;
  let ticking = false;
  const update = () => {
    ticking = false;
    const y = window.scrollY;
    head.classList.toggle('is-scrolled', y > 80);
    const delta = y - lastY;
    const busy = openStack.length > 0 || head.querySelector('details[open]') || head.contains(doc.activeElement);
    if (y > 600 && delta > 4 && !busy) head.classList.add('is-hidden');
    else if (delta < -4 || y <= 600) head.classList.remove('is-hidden');
    lastY = y;
  };
  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true }
  );
  head.addEventListener('focusin', () => head.classList.remove('is-hidden'));
  update();
  if (!W.qs('.hero')) W.qs('.header--overlay')?.classList.remove('header--overlay');
}

/* ---------- Shop panel (disclosure on <details>) ---------- */
W.qsa('[data-shop-menu]').forEach((details) => {
  const summary = details.querySelector('summary');
  const panel = details.querySelector('.shop-panel');
  const images = W.qsa('[data-panel-image]', details);
  const closePanel = async (returnFocus) => {
    if (!details.open) return;
    await W.finished(W.animate(panel, [{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: W.ease.in, fill: 'forwards' }));
    details.open = false;
    panel.getAnimations().forEach((animation) => animation.cancel());
    if (returnFocus) summary.focus();
  };
  summary.addEventListener('click', (event) => {
    if (details.open) {
      event.preventDefault();
      closePanel(false);
    }
  });
  details.addEventListener('toggle', () => {
    if (!details.open) return;
    W.header.show();
    W.animate(panel, [{ clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)' }], { duration: 520, easing: W.ease.out });
    W.qsa('.shop-panel__list li', panel).forEach((item, index) => {
      W.animate(item, [{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }], {
        duration: 420,
        delay: 80 + Math.min(index, 6) * 50,
        easing: W.ease.out,
        fill: 'backwards',
      });
    });
  });
  details.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && details.open) {
      event.preventDefault();
      closePanel(true);
    }
  });
  details.addEventListener('focusout', (event) => {
    if (details.open && event.relatedTarget && !details.contains(event.relatedTarget)) closePanel(false);
  });
  doc.addEventListener('click', (event) => {
    if (details.open && !details.contains(event.target)) closePanel(false);
  });
  const activate = (index) => {
    images.forEach((image) => image.classList.toggle('is-active', image.dataset.panelImage === index));
  };
  details.addEventListener('pointerover', (event) => {
    const link = event.target.closest('[data-panel-index]');
    if (link) activate(link.dataset.panelIndex);
  });
  details.addEventListener('focusin', (event) => {
    const link = event.target.closest('[data-panel-index]');
    if (link) activate(link.dataset.panelIndex);
  });
});

/* ---------- Accordions: height animation where ::details-content can't do it ---------- */
if (!(window.CSS && CSS.supports('interpolate-size', 'allow-keywords'))) {
  doc.addEventListener('click', (event) => {
    const summary = event.target.closest('.accordion > summary');
    if (!summary || W.reducedMotion()) return;
    const details = summary.parentElement;
    const content = details.querySelector('.accordion__content');
    if (!content) return;
    event.preventDefault();
    content.getAnimations().forEach((animation) => animation.cancel());
    if (details.open) {
      const height = content.offsetHeight;
      const closing = content.animate([{ height: `${height}px` }, { height: '0px' }], { duration: 300, easing: W.ease.in });
      closing.onfinish = () => {
        details.open = false;
      };
    } else {
      details.open = true;
      const height = content.offsetHeight;
      content.animate([{ height: '0px' }, { height: `${height}px` }], { duration: 420, easing: W.ease.out });
    }
  });
}

/* ---------- Quantity steppers ---------- */
const rollInput = (input, previous) => {
  if (W.reducedMotion()) return;
  const view = input.parentElement;
  const ghost = doc.createElement('span');
  ghost.className = 'qty__ghost';
  ghost.setAttribute('aria-hidden', 'true');
  ghost.textContent = previous;
  view.appendChild(ghost);
  ghost.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-100%)', opacity: 0 }], {
    duration: 180,
    easing: W.ease.in,
    fill: 'forwards',
  }).onfinish = () => ghost.remove();
  input.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], { duration: 180, easing: W.ease.out });
};

const syncStepper = (stepper) => {
  const input = stepper.querySelector('.qty__input');
  const minus = stepper.querySelector('[data-qty-step="-1"]');
  const plus = stepper.querySelector('[data-qty-step="1"]');
  if (!input || !minus) return;
  const min = Number(input.min || 0);
  minus.setAttribute('aria-disabled', String(Number(input.value) <= min));
  if (plus) plus.setAttribute('aria-disabled', String(Boolean(input.max) && Number(input.value) >= Number(input.max)));
};
W.qsa('[data-qty]').forEach(syncStepper);
W.syncSteppers = (scope = doc) => W.qsa('[data-qty]', scope).forEach(syncStepper);

doc.addEventListener('click', (event) => {
  const button = event.target.closest('[data-qty-step]');
  if (!button) return;
  const stepper = button.closest('[data-qty]');
  const input = stepper && stepper.querySelector('.qty__input');
  if (!input) return;
  const min = Number(input.min || 0);
  const max = input.max ? Number(input.max) : Infinity;
  const previous = Number(input.value) || 0;
  const increment = Number(input.step) || 1;
  const next = Math.min(max, Math.max(min, previous + Number(button.dataset.qtyStep) * increment));
  if (next === previous) return;
  input.value = next;
  rollInput(input, previous);
  syncStepper(stepper);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});
doc.addEventListener('change', (event) => {
  const stepper = event.target.closest('[data-qty]');
  if (stepper) syncStepper(stepper);
});

/* bfcache: a restored page must not keep a locked root. */
window.addEventListener('pageshow', (event) => {
  if (event.persisted && !openStack.length) root.classList.remove('is-locked');
});
